// src/plate/containment.js
import { MODES } from "../data/decomposition";

/**
 * A node with children is a closed assembly. Children are absent from the
 * scene until it opens, and an edge reaching into a closed assembly is
 * redrawn to its outermost closed ancestor — the structure never lies, it
 * states itself at a coarser grain.
 *
 * Containment also emits a real edge when open: without one the children have
 * no force holding them to what they belong to and drift away from the parent.
 */
export function createContainment(nodes, links) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const kids = new Map();
  nodes.forEach((n) => {
    if (!n.parent) return;
    if (!kids.has(n.parent)) kids.set(n.parent, []);
    kids.get(n.parent).push(n.id);
  });
  // A node declares defaultOpen:true to start open (only if it has children).
  const open = new Set(nodes.filter((n) => n.defaultOpen && kids.has(n.id)).map((n) => n.id));

  const childCount = (id) => (kids.get(id) || []).length;
  const isOpen = (id) => open.has(id);

  const isVisible = (n) => {
    if (n.unknown) return false; // lives in the margin, not the model
    let p = n.parent;
    while (p) {
      if (!open.has(p) || byId.get(p).unknown) return false; // unplaced parent hides its contents
      p = byId.get(p).parent;
    }
    return true;
  };

  const visibleAncestor = (id) => {
    // Walk the whole chain: an open inner assembly inside a closed outer one
    // is still hidden, so the edge lands on the outermost closed ancestor.
    let n = byId.get(id);
    if (!n) return id;
    let best = n.id;
    while (n && n.parent) {
      if (!open.has(n.parent)) best = n.parent;
      n = byId.get(n.parent);
    }
    return best;
  };

  function visibleGraph() {
    const vis = nodes.filter(isVisible);
    const visIds = new Set(vis.map((n) => n.id));
    const seen = new Set();
    const out = [];
    const push = (source, target, mode) => {
      const key = `${source}\u0000${target}\u0000${mode}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ source, target, mode });
    };

    vis.forEach((n) => {
      if (n.parent && open.has(n.parent) && visIds.has(n.parent)) push(n.parent, n.id, MODES.CONTAINS);
    });

    links.forEach((l) => {
      const a = visibleAncestor(l.source);
      const b = visibleAncestor(l.target);
      if (a === b) return;
      if (!visIds.has(a) || !visIds.has(b)) return; // an unplaced endpoint
      push(a, b, l.mode);
    });

    return { nodes: vis, links: out };
  }

  function toggle(id) {
    if (!childCount(id)) return false;
    if (open.has(id)) open.delete(id); else open.add(id);
    return true;
  }

  return { childCount, isOpen, toggle, visibleGraph };
}
